import { matchPath } from 'react-router';
import { createSelector } from 'reselect';

import {
  enrichChecklists,
  splitChecklistsByType,
} from 'components/interventions/detail/utils/InterventionFormUtils';
import { PATH } from 'constants/RoutesConstants';
import { USER_TYPE } from 'constants/UserConstants';
import { ENTITY_TYPE } from 'modules/app/constants';
import { getCurrentUserIsAdmin, getCurrentUserIsReadOnly } from 'modules/users/selectors/currentUser';
import { getSelectedDealerSMSProvider } from 'selectors/dealerSelectors';
import { getCurrentLocation } from 'selectors/router';
import { isElementBefore } from 'utils/ArrayUtils';

import { getRoot, getCampaignById, getUIState, getStatisticsById } from '.';
import { CAMPAIGN_EVENT_TYPE_ENUM, CAMPAIGN_STATUS_ENUM, DEFAULT_STATUS, TRIGGERED_CAMPAIGN_STATUSES } from '../constants';
import { getActivatedChannels } from '../utils';

const getDetailsState = createSelector(getUIState, state => state.details);

export const getSelectedCampaignId = createSelector(getCurrentLocation, location => {
  const match = matchPath(PATH.CAMPAIGNS_DETAILS.path, location);
  return match?.params?.campaignId;
});

export const getSelectedCampaign = createSelector(getRoot, getSelectedCampaignId, getCampaignById);

export const allowSMS = createSelector(getSelectedDealerSMSProvider, () => false); // TODO SMS CAMPAIGN (smsProvider) => smsProvider === SMS_PROVIDER.CMCOM);

export const getInitialValues = createSelector(getSelectedCampaign, allowSMS, (campaign, allowSMSChannel) => {
  const hasSelectedChannels = campaign?.sms?.enabled || campaign?.email?.enabled;
  const forceEmail = !allowSMSChannel || !hasSelectedChannels;

  return {
    ...campaign,
    email: {
      ...campaign?.email,
      enabled: forceEmail ? true : campaign?.email?.enabled,
    },
    ...splitChecklistsByType(enrichChecklists(campaign?.checklists, { mode: campaign?.mode })),
  };
});

const getLoadingState = createSelector(getDetailsState, state => state.loading);

export const patchingStatus = createSelector(getLoadingState, state => state.patchStatus);

export const patchingName = createSelector(getLoadingState, state => state.patchName);

export const saving = createSelector(getLoadingState, state => state.save);

export const exporting = createSelector(getLoadingState, state => state.export);

export const deleting = createSelector(getLoadingState, state => state.delete);

export const initialized = createSelector(getDetailsState, state => state.initialized);

export const canSaveSelectedCampaign = createSelector(
  getSelectedCampaign,
  getCurrentUserIsReadOnly,
  ({ status }, readonlyUser) => !readonlyUser && status !== CAMPAIGN_STATUS_ENUM.CLOSED
);

export const isSelectedCampaignReadonly = createSelector(
  getSelectedCampaign,
  getCurrentUserIsReadOnly,
  ({ status }, readonlyUser) => readonlyUser || status === CAMPAIGN_STATUS_ENUM.CLOSED
);

export const getSelectedCampaignMode = createSelector(getSelectedCampaign, ({ mode }) => mode);

export const isSelectedCampaignTriggered = createSelector(getSelectedCampaign, campaign =>
  TRIGGERED_CAMPAIGN_STATUSES.includes(campaign?.status));

export const isSelectedCampaignSending = createSelector(
  getSelectedCampaign,
  campaign => campaign?.status === CAMPAIGN_STATUS_ENUM.SENDING
);

export const isSelectedCampaignStopped = createSelector(
  getSelectedCampaign,
  campaign => campaign?.status === CAMPAIGN_STATUS_ENUM.STOPPED
);

export const isSelectedCampaignClosed = createSelector(
  getSelectedCampaign,
  campaign => campaign?.status === CAMPAIGN_STATUS_ENUM.CLOSED
);

const getSubEntityId = (entity, field) => entity?.[`${entity}Id`] ?? entity?.[field]?.id;

export const canSendCampaign = createSelector(getSelectedCampaign, getCurrentUserIsReadOnly, (campaign, isReadonly) => {
  if (isReadonly || !isElementBefore(DEFAULT_STATUS, campaign.status, CAMPAIGN_STATUS_ENUM.ERROR)) {
    return false;
  }

  if (campaign.sms?.enabled && !campaign.sms.content) {
    return false;
  }

  if (campaign.email?.enabled && !campaign.email.subject) {
    return false;
  }

  const contactListId = getSubEntityId(campaign, 'contactList');
  const contactPersonId = getSubEntityId(campaign, 'contactPerson');

  return Boolean(contactListId && contactPersonId && campaign.content && campaign.expirationDate);
});

export const getSocketSubscription = createSelector(getSelectedCampaignId, campaignId => ({
  entityType: ENTITY_TYPE.CAMPAIGN,
  entityId: campaignId,
}));

export const getSelectedStatistics = createSelector(getRoot, getSelectedCampaignId, getStatisticsById);

export const getConsumedStatistics = createSelector(
  getSelectedCampaign,
  getSelectedStatistics,
  (campaign, statistics) => {
    if (statistics) {
      return Object.entries(statistics).reduce(
        (acc, [channel, { numberSent: value }]) => [...acc, [channel, value]],
        []
      );
    }
    return getActivatedChannels(campaign).map(channel => [channel, 0]);
  }
);

const getExportCampaignId = createSelector(getDetailsState, state => state.export.campaignId);
export const isContentModalOpen = createSelector(getDetailsState, state => state.contentModalOpen);

export const isExportModalOpen = createSelector(getExportCampaignId, campaignId => Boolean(campaignId));

export const isImportingChecklists = createSelector(getDetailsState, state => state.importChecklistsLoading);

export const getCampaignPreviewUrl = createSelector(getDetailsState, state => state.preview.campaign);

export const canFollowCampaign = createSelector(
  getSelectedCampaign,
  getCurrentUserIsAdmin,
  getCurrentUserIsReadOnly,
  ({ status, interventionsBulkInProgress }, isAdmin, isReadOnly) =>
    !interventionsBulkInProgress && status !== CAMPAIGN_STATUS_ENUM.SENDING && !isAdmin && !isReadOnly
);

export const isFollowLoading = createSelector(getUIState, ({ details }) => details.loading.follow || details.loading.unfollow);

export const isCampaignStoppedBySystem = createSelector(
  isSelectedCampaignStopped,
  getSelectedCampaign,
  (isStopped, campaign) => {
    const lastEvent = campaign.events?.find(event => event.type === CAMPAIGN_EVENT_TYPE_ENUM.SENDING_STOPPED);
    return isStopped && lastEvent?.createdBy.type === USER_TYPE.SYSTEM;
  }
);
